import { create } from 'zustand'
import type {} from '@redux-devtools/extension' // required for devtools typing
import { devtools } from 'zustand/middleware'
import type { BlockMessage } from '../types'
import type { Client } from '@botpress/webchat-client'
import { integrationMessageToBlockMessage } from '../adapters'

type MessagesStore = {
  messages: BlockMessage[]
  isFetching: boolean
  setMessages: (messages: BlockMessage[]) => void
  saveMessage: (message: BlockMessage) => void
  clearMessages: () => void
  updateMessage: (messageId: string, message: Partial<BlockMessage>) => void
  fetchMessages: (props: { client: Client; conversationId: string }) => Promise<BlockMessage[]>
}

export const useMessagesStore = create<MessagesStore>()(
  devtools(
    (set) => ({
      messages: [],
      isFetching: false,
      setMessages: (messages) => set({ messages }),
      saveMessage: (message) =>
        set(({ messages }) => {
          const clientMessageId = message.metadata?.clientMessageId

          // if the message is already in the store, update it while keeping the same order
          if (clientMessageId) {
            const existingMessageIndex = messages.findIndex((m) => m.metadata?.clientMessageId === clientMessageId)
            const existingMessage = messages[existingMessageIndex]

            if (existingMessageIndex !== -1) {
              return {
                messages: [
                  ...messages.slice(0, existingMessageIndex),
                  { ...existingMessage, ...message },
                  ...messages.slice(existingMessageIndex + 1),
                ],
              }
            }
          }
          return { messages: [...messages.filter((m) => m.id !== message.id), message] }
        }),
      updateMessage: (messageId, message) =>
        set(({ messages }) => ({
          messages: messages.map((m) => (m.id === messageId ? { ...m, ...message } : m)),
        })),
      clearMessages: () => set({ messages: [] }),
      fetchMessages: async ({ client, conversationId }) => {
        const allMessages = []
        set({ isFetching: true })
        let nextToken: string | undefined = undefined
        do {
          const { messages, meta } = await client.listConversationMessages({ conversationId, nextToken })
          allMessages.push(...messages)
          nextToken = meta.nextToken
        } while (nextToken)

        set({ isFetching: false })

        return allMessages
          .reverse()
          .map((message) => integrationMessageToBlockMessage(message))
          .filter(Boolean)
      },
    }),
    { name: 'messagesStore' }
  )
)
